"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ChevronRight, Play } from "lucide-react"
import ProductVideo from "../video/product-video"

interface Category {
  id: number
  name: string
  image: string
  meta: {
    title: string
    description: string
    keywords: string
  }
  subcategories?: Array<{
    id: string
    name: string
    meta: {
      title: string
      description: string
      keywords: string
    }
  }>
}

interface MobileCategoryCardProps {
  category: Category
  index?: number
}

export default function MobileCategoryCard({ category, index = 0 }: MobileCategoryCardProps) {
  const [showVideo, setShowVideo] = useState(false)

  if (!category || !category.meta) {
    return null
  }

  const slug = category.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card className="overflow-hidden bg-white shadow-lg border-0 rounded-2xl">
        <CardContent className="p-0">
          <Link href={`/categories/${slug}`}>
                    <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg" aria-label={`View details for ${category.name}`}> 
              {showVideo ? (
                <ProductVideo productType={category.name} />
              ) : (
                <div className="relative w-full h-full">
                  <Image
                    src={`${category.image}`}
                    alt={category.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </div>
              )}

              {/* Video Toggle */}
              {/* <motion.button
                className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-full text-white"
                onClick={(e) => {
                  e.preventDefault()
                  setShowVideo(!showVideo)
                }}
                whileTap={{ scale: 0.9 }}
              >
                <Play className="h-4 w-4" />
              </motion.button> */}

              {/* Category Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <h3 className="font-bold text-xl text-white mb-2">{category.name}</h3>
                {category.subcategories && category.subcategories.length > 0 && (
                  <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                    {category.subcategories.length} types
                  </Badge>
                )}
              </div>
            </div>

            <div className="p-4">
              <p className="text-slate-600 text-sm line-clamp-2 mb-3 leading-relaxed">
                {category.meta?.description || "No description available"}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center text-slate-900 font-medium">
                  <span className="text-sm">Explore Products</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
                {category.subcategories && category.subcategories.length > 0 && (
                  <div className="text-xs text-slate-500">{category.subcategories.length} categories</div>
                )}
              </div>
            </div>
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  )
}
